import {AliffHelpers, DocumentService} from "@buildwithflux/core";
import {
    AliffExportTimeError,
    AliffExportTimeErrorCode,
    AliffExportTimeErrorDifferentialPairsPinsMismatch,
    AliffExportTimeErrorPolygonProtected,
    AliffExportTimeErrorPowerLayerAvoided,
    AliffExportTimeErrorUnsupportedViaConfig,
    AnyPcbBakedNode,
    AutoLayoutPreflightAnomaly,
    AutoLayoutPreflightAnomalyByCode,
    DocumentUid,
    PcbBakedNode,
    PcbNodeTypes,
    PcbNodesMap,
    Review,
    ReviewAvailability,
    ReviewCheck,
    ReviewColumn,
    ReviewStatus,
    ReviewUid,
    reviewData,
} from "@buildwithflux/models";
import {ReviewRepository} from "@buildwithflux/repositories";
import {Logger} from "@buildwithflux/shared";

import {
    generateComponentLink,
    getLabelForPcbNode,
} from "components/pages/document/components/editor/components/right_drawer/review/helpers/convertDRCResultToReview";
import {AnalyticsStorage} from "modules/storage_engine/AnalyticsStorage";
import {SurfaceBasedTrackingEvents} from "@buildwithflux/models";
import {getAllBakedNodes} from "modules/stores/pcb/utils";

import type {AutoLayoutEventBus} from "./AutoLayoutEventBus";
import type {AutoLayoutReviewService} from "./AutoLayoutReviewService";
import type {AutoLayoutStoreService} from "./AutoLayoutStoreService";
import {DeepPcbAutoLayoutBaseService} from "./DeepPcbAutoLayoutBaseService";
import type {DeepPcbAutoLayoutEventMap} from "./DeepPcbAutoLayoutEventBus";

/**
 * DeepPCB implementation of auto-layout review service for the new infrastructure.
 * This service is stateless and uses the event bus for notifications.
 */
export class DeepPcbAutoLayoutReviewService extends DeepPcbAutoLayoutBaseService implements AutoLayoutReviewService {
    private readonly reviewUid: ReviewUid = `rev_system_checks_auto_layout`;

    constructor(
        eventBus: AutoLayoutEventBus<DeepPcbAutoLayoutEventMap>,
        logger: Logger,
        private readonly autoLayoutStoreService: AutoLayoutStoreService,
        private readonly documentService: DocumentService,
        private readonly reviewRepository: ReviewRepository,
        private readonly analyticsStorage: AnalyticsStorage,
    ) {
        super("AutoLayoutReviewService", eventBus, logger);
    }

    /**
     * Initiates the auto-layout review process.
     */
    public async evaluate(
        objectIdToNodeUidMap: Record<string, string>,
        exportTimeErrors: AliffExportTimeError[],
    ): Promise<void> {
        const currentJob = this.autoLayoutStoreService.getCurrentJob();
        if (!currentJob?.jobUid) {
            throw new Error("No active auto-layout job found");
        }

        this.emit("review.evaluation.started", {jobUid: currentJob.jobUid});

        try {
            const documentUid = this.documentService.snapshot().uid;

            // First, get the existing review to use for creating the initial review with export time errors
            const existingReview = await this.reviewRepository.getByUid(documentUid, this.reviewUid);

            // Create a single review factory
            const reviewFactory = new AutoLayoutReviewFactory(
                documentUid,
                getAllBakedNodes(this.documentService),
                objectIdToNodeUidMap,
            );

            // Create and save a review with just the export time errors immediately
            let updatedReview = reviewFactory.createReview(
                [], // No preflight anomalies yet
                exportTimeErrors,
                existingReview,
            );

            // Save the initial review to the db
            await this.saveReview(updatedReview);

            // Get preflight anomalies from the client job
            const preflightAnomalies = await this.getPreflightAnomalies();

            // Get the updated review with both export time errors and preflight anomalies
            updatedReview = reviewFactory.createReview(preflightAnomalies, exportTimeErrors, updatedReview);

            // Save the final review to the db
            await this.saveReview(updatedReview);

            await this.trackReviewResults(updatedReview, preflightAnomalies, exportTimeErrors);

            this.emit("review.evaluation.completed", {
                jobUid: currentJob.jobUid,
                reviewUid: this.reviewUid,
            });
        } catch (error) {
            this.logger.error("Failed to evaluate auto-layout review", {error});
            this.emit("review.evaluation.failed", {
                jobUid: currentJob.jobUid,
                error,
            });
        }
    }

    public async reset(): Promise<void> {
        await this.reviewRepository.resetReview(this.documentService.snapshot().uid, this.reviewUid);
    }

    private async getPreflightAnomalies(): Promise<AutoLayoutPreflightAnomaly[]> {
        return new Promise<AutoLayoutPreflightAnomaly[]>((resolve) => {
            const currentJob = this.autoLayoutStoreService.getCurrentJob();
            if (!currentJob) {
                throw new Error("No active auto-layout job found");
            }

            const preflightAnomalies = currentJob.preflightAnomalies;
            if (preflightAnomalies) {
                resolve(preflightAnomalies);
                return;
            }

            const unsubscribe = this.autoLayoutStoreService.getStore().subscribe((state) => {
                if (state.currentJob?.preflightAnomalies) {
                    resolve(state.currentJob.preflightAnomalies);
                    unsubscribe();
                }
            });
        });
    }

    private saveReview(review: Review) {
        const documentUid = this.documentService.snapshot().uid;
        return this.reviewRepository.saveReview(documentUid, {
            ...review,
            createdAt: review.createdAt ?? Date.now(),
            updatedAt: Date.now(),
        });
    }

    private async trackReviewResults(
        review: Review,
        preflightAnomalies: AutoLayoutPreflightAnomaly[],
        exportTimeErrors: AliffExportTimeError[],
    ) {
        await this.analyticsStorage.logEvent(SurfaceBasedTrackingEvents.deeppcbReview, {
            review_uid: review.uid,
            document_uid: review.documentUid,
            status: review.status,
            preflight_anomaly_count: preflightAnomalies.length,
            export_error_count: exportTimeErrors.length,
        });

        // Track individual preflight anomalies
        for (const anomaly of preflightAnomalies) {
            await this.analyticsStorage.logEvent(SurfaceBasedTrackingEvents.deeppcbPreflightAnomaly, {
                review_uid: review.uid,
                document_uid: review.documentUid,
                anomaly_code: anomaly.code,
                anomaly_original_code: anomaly.originalCode,
                anomaly_data: JSON.stringify(anomaly.data),
            });
        }

        // Track individual export errors
        for (const error of exportTimeErrors) {
            await this.analyticsStorage.logEvent(SurfaceBasedTrackingEvents.deeppcbExportError, {
                review_uid: review.uid,
                document_uid: review.documentUid,
                error_code: error.code,
                error_data: JSON.stringify(error.data),
            });
        }
    }
}

class AutoLayoutReviewFactory {
    private existingReview: Review | undefined;

    constructor(
        private readonly documentUid: DocumentUid,
        private readonly nodes: PcbNodesMap<AnyPcbBakedNode>,
        private readonly objectIdToNodeUidMap: Record<string, string>,
    ) {}

    public createReview(
        preflightAnomalies: AutoLayoutPreflightAnomaly[],
        exportTimeErrors: AliffExportTimeError[],
        existingReview: Review | undefined,
    ): Review {
        this.existingReview = existingReview;

        const autoLayoutReviewStructure = reviewData.system_checks_auto_layout;

        let index = 0;
        const autoLayoutReviewChecks: Record<string, ReviewCheck> = {};

        // Create checks for preflight anomalies
        for (const anomaly of preflightAnomalies) {
            const check = this.createAnomalyCheck(anomaly);
            if (!check) continue;
            autoLayoutReviewChecks[check.uid] = {...check, index: index++};
        }

        // Create checks for export-time errors
        for (const error of exportTimeErrors) {
            const check = this.createExportTimeErrorCheck(error);
            if (!check) continue;
            autoLayoutReviewChecks[check.uid] = {...check, index: index++};
        }

        const autoLayoutReview: Review = {
            // Populate the review with the default structure
            ...autoLayoutReviewStructure,

            // Add more fields to the review
            uid: `rev_system_checks_auto_layout`,
            availability: ReviewAvailability.BETA,
            documentUid: this.documentUid,
            columns: Object.fromEntries(
                Object.keys(autoLayoutReviewStructure.columns).map((key) => [
                    key,
                    {...autoLayoutReviewStructure.columns[key], uid: key} as ReviewColumn,
                ]),
            ),
            assignedStatus: undefined,
            status: ReviewStatus.OPEN,
            createdAt: Date.now(),

            // Override with existing review data (statuses)
            ...(existingReview ?? {}),

            // Add the checks and update the updatedAt field
            checks: autoLayoutReviewChecks,
            updatedAt: Date.now(),
        };

        return autoLayoutReview;
    }

    private createAnomalyCheck(anomaly: AutoLayoutPreflightAnomaly): Omit<ReviewCheck, "index"> | undefined {
        switch (anomaly.code) {
            case "ROUTING_UNROUTABLE_PIN":
                return this.createAnomalyCheckUnroutablePin(anomaly);
            case "DRC_OVERLAPPING_PINS_DIFFERENT_NETS":
                return this.createAnomalyCheckOverlappingPinsDifferentNets(anomaly);
            case "ROUTING_UNROUTABLE_DIFFERENTIAL_PAIR_PINS":
                return this.createAnomalyCheckUnroutableDifferentialPairPins(anomaly);
            case "ROUTING_PIN_INSIDE_KEEPOUT":
                return this.createAnomalyCheckPinInsideKeepout(anomaly);
            default:
                return undefined;
        }
    }

    private createAnomalyCheckUnroutablePin(
        anomaly: AutoLayoutPreflightAnomalyByCode<"ROUTING_UNROUTABLE_PIN">,
    ): Omit<ReviewCheck, "index"> | undefined {
        const [padNode] = this.getNodesFromIds([anomaly.data.pinId], PcbNodeTypes.pad);
        if (!padNode) return undefined;

        const {
            designators: [designator],
            designatorUidMap,
        } = this.getDesignatorData([padNode]);
        if (!designator) return undefined;

        return this.createCheck(
            {
                uid: this.getAnomalyUid(anomaly),
                name: designator,
                description: `${generateComponentLink(designator)} is blocked and cannot be routed by Auto-Layout.`,
                designator,
                designatorUidMap,
                link: "",
                data: {
                    object: designator,
                    issue: "Unroutable",
                    cause: "Pad blocked",
                },
            },
            ReviewStatus.WARNING,
        );
    }

    private createAnomalyCheckPinInsideKeepout(
        anomaly: AutoLayoutPreflightAnomalyByCode<"ROUTING_PIN_INSIDE_KEEPOUT">,
    ): Omit<ReviewCheck, "index"> | undefined {
        const [padNode] = this.getNodesFromIds([anomaly.data.pinId], PcbNodeTypes.pad);
        if (!padNode) return undefined;

        const {
            designators: [designator],
            designatorUidMap,
        } = this.getDesignatorData([padNode]);
        if (!designator) return undefined;

        return this.createCheck(
            {
                uid: this.getAnomalyUid(anomaly),
                name: designator,
                description: `${generateComponentLink(
                    designator,
                )} is inside a zone keepout. The keepout will be ignored when routing this pad.`,
                designator,
                designatorUidMap,
                link: "",
                data: {
                    object: designator,
                    issue: "Inside keepout",
                    cause: "Keepout ignored",
                },
            },
            ReviewStatus.WARNING,
        );
    }

    private createAnomalyCheckOverlappingPinsDifferentNets(
        anomaly: AutoLayoutPreflightAnomalyByCode<"DRC_OVERLAPPING_PINS_DIFFERENT_NETS">,
    ): Omit<ReviewCheck, "index"> | undefined {
        const padNodes = this.getNodesFromIds(anomaly.data.pinIds, PcbNodeTypes.pad);
        if (padNodes.length === 0) return undefined;

        const {designatorsString, designatorUidMap} = this.getDesignatorData(padNodes);

        return this.createCheck(
            {
                uid: this.getAnomalyUid(anomaly),
                name: designatorsString,
                description: `${generateComponentLink(
                    designatorsString,
                )} overlaps with copper from another net and so cannot be routed by Auto-Layout.`,
                designator: designatorsString,
                designatorUidMap,
                link: "",
                data: {
                    object: designatorsString,
                    issue: "Unroutable",
                    cause: "Overlapping pads",
                },
            },
            ReviewStatus.WARNING,
        );
    }

    private createAnomalyCheckUnroutableDifferentialPairPins(
        anomaly: AutoLayoutPreflightAnomalyByCode<"ROUTING_UNROUTABLE_DIFFERENTIAL_PAIR_PINS">,
    ): Omit<ReviewCheck, "index"> | undefined {
        const padNodes = this.getNodesFromIds(anomaly.data.pinIds, PcbNodeTypes.pad);
        const [netNode1, netNode2] = padNodes
            .map((padNode) => this.nodes[padNode.bakedRules.effectiveNetId ?? ""])
            .filter((node): node is PcbBakedNode<PcbNodeTypes.net> => node?.type === PcbNodeTypes.net);
        if (!netNode1 || !netNode2) return undefined;

        const {designatorsString: padDesignatorsString, designatorUidMap: padDesignatorUidMap} =
            this.getDesignatorData(padNodes);
        const {designatorsString: netDesignatorsString, designatorUidMap: netDesignatorUidMap} = this.getDesignatorData(
            [netNode1, netNode2],
        );

        return this.createCheck(
            {
                uid: this.getAnomalyUid(anomaly),
                name: netDesignatorsString,
                description: `Pads ${generateComponentLink(
                    padDesignatorsString,
                )} in the differential pair ${generateComponentLink(
                    netDesignatorsString,
                )} is blocked, so none of the pads in the pair can be routed by Auto-Layout.`,
                designator: netDesignatorsString,
                designatorUidMap: {
                    ...padDesignatorUidMap,
                    ...netDesignatorUidMap,
                },
                link: "",
                data: {
                    object: netDesignatorsString,
                    issue: "Unroutable",
                    cause: "Wired incorrectly",
                },
            },
            ReviewStatus.WARNING,
        );
    }

    private createExportTimeErrorCheck(error: AliffExportTimeError): Omit<ReviewCheck, "index"> | undefined {
        switch (error.code) {
            case AliffExportTimeErrorCode.DIFFERENTIAL_PAIRS_PINS_MISMATCH:
                return this.createExportTimeErrorCheckDifferentialPairsPinsMismatch(error);
            case AliffExportTimeErrorCode.UNSUPPORTED_VIA_CONFIG:
                return this.createExportTimeErrorCheckUnsupportedViaConfig(error);
            case AliffExportTimeErrorCode.POWER_LAYER_AVOIDED:
                return this.createExportTimeErrorCheckPowerLayerAvoided(error);
            case AliffExportTimeErrorCode.POLYGON_PROTECTED:
                return this.createExportTimeErrorCheckPolygonProtected(error);
            default:
                return undefined;
        }
    }

    private createExportTimeErrorCheckDifferentialPairsPinsMismatch(
        error: AliffExportTimeErrorDifferentialPairsPinsMismatch,
    ): Omit<ReviewCheck, "index"> | undefined {
        const [netNode1, netNode2] = this.getNodesFromIds(error.data.nets, PcbNodeTypes.net);
        if (!netNode1 || !netNode2) return undefined;

        const {designatorsString, designatorUidMap} = this.getDesignatorData([netNode1, netNode2]);

        return this.createCheck(
            {
                uid: this.getAnomalyUid(error),
                name: designatorsString,
                description: `The differential pair ${generateComponentLink(
                    designatorsString,
                )} is not wired correctly on Schematic, and so none of the pads in the pair can be routed by Auto-Layout.`,
                designator: designatorsString,
                designatorUidMap,
                link: "",
                data: {
                    object: designatorsString,
                    issue: "Unroutable",
                    cause: "Wired incorrectly",
                },
            },
            ReviewStatus.WARNING,
        );
    }

    private createExportTimeErrorCheckUnsupportedViaConfig(
        error: AliffExportTimeErrorUnsupportedViaConfig,
    ): Omit<ReviewCheck, "index"> | undefined {
        const viaConfig = error.data.viaConfig;

        return this.createCheck(
            {
                uid: this.getAnomalyUid(error),
                name: viaConfig.name,
                description: `The Via Config ${viaConfig.name} is not used by Auto-Layout because Micro-vias are not yet supported.`,
                designator: viaConfig.name,
                link: "",
                data: {
                    object: viaConfig.name,
                    issue: "Ignored",
                    cause: "Unsupported Via Config",
                },
            },
            ReviewStatus.INFORMATIONAL,
        );
    }

    private createExportTimeErrorCheckPowerLayerAvoided(
        error: AliffExportTimeErrorPowerLayerAvoided,
    ): Omit<ReviewCheck, "index"> | undefined {
        const layerId = error.data.layerId;

        return this.createCheck(
            {
                uid: this.getAnomalyUid(error),
                name: layerId,
                description: `The layer ${layerId} is avoided by Auto-Layout because its type is Power. To let Auto-Layout route on this layer, open then Stackup to change this layer's type to Signal.`,
                designator: layerId,
                link: "",
                data: {
                    object: layerId,
                    issue: "Avoided",
                    cause: "Power layer",
                },
            },
            ReviewStatus.INFORMATIONAL,
        );
    }

    private createExportTimeErrorCheckPolygonProtected(
        error: AliffExportTimeErrorPolygonProtected,
    ): Omit<ReviewCheck, "index"> | undefined {
        const [polygonNode] = this.getNodesFromIds([error.data.polygonNodeUid], PcbNodeTypes.polygon);
        if (!polygonNode) return undefined;

        const {designatorsString, designatorUidMap} = this.getDesignatorData([polygonNode]);

        return this.createCheck(
            {
                uid: this.getAnomalyUid(error),
                name: designatorsString,
                description: `Auto-Layout will protect ${generateComponentLink(
                    designatorsString,
                )} from intersecting with traces or vias from other nets.`,
                designator: designatorsString,
                designatorUidMap,
                link: "",
                data: {
                    object: designatorsString,
                    issue: "Protected",
                    cause: "Protected rule automatically applied",
                },
            },
            ReviewStatus.INFORMATIONAL,
        );
    }

    private createCheck(
        checkData: Pick<
            ReviewCheck,
            "uid" | "name" | "description" | "designator" | "designatorUidMap" | "data" | "link"
        >,
        defaultStatus: ReviewStatus,
    ): Omit<ReviewCheck, "index"> {
        return {
            ...checkData,

            createdAt: this.existingReview?.checks[checkData.uid]?.createdAt ?? Date.now(),
            updatedAt: Date.now(),

            reviewUid: `rev_system_checks_auto_layout`,
            documentUid: this.documentUid,

            status: this.existingReview?.checks[checkData.uid]?.status ?? defaultStatus,
            assignedStatus: this.existingReview?.checks[checkData.uid]?.assignedStatus,
        };
    }

    private getNodesFromIds<NodeType extends PcbNodeTypes = PcbNodeTypes>(
        uids: Array<string | undefined>,
        nodeType?: NodeType,
    ): PcbBakedNode<NodeType>[] {
        return uids.reduce<Array<PcbBakedNode<NodeType>>>((nodes, uidOrUndefined) => {
            const uid = uidOrUndefined as string;
            const node = this.nodes[uid] ?? this.nodes[this.objectIdToNodeUidMap[uid] ?? ""];
            if (node && nodeType ? node.type === nodeType : true) {
                nodes.push(node as PcbBakedNode<NodeType>);
            }
            return nodes;
        }, []);
    }

    private getDesignatorData(nodes: AnyPcbBakedNode[]) {
        const designatorUidMap: Record<string, string> = {};
        const designators = nodes.map((node) => {
            if (node.type === PcbNodeTypes.pad) {
                const componentUid = node.uid.split("_")[0] ?? "";
                const componentNode = this.nodes[componentUid];
                const component = getLabelForPcbNode(componentNode);

                if (!component || component === "Unknown") {
                    const designator = node.name || node.uid;
                    designatorUidMap[designator] = node.uid;
                    return designator;
                }

                const padBakedNode = node as PcbBakedNode<PcbNodeTypes.pad>;
                const terminalId = padBakedNode.terminalId;
                const nodeLabel = getLabelForPcbNode(node);

                let pinInfo: string;
                if (nodeLabel && nodeLabel.trim() !== "") {
                    pinInfo = nodeLabel;
                } else if (terminalId && terminalId.trim() !== "") {
                    pinInfo = terminalId;
                } else {
                    pinInfo = node.uid.substring(0, 8);
                }

                const designator = `${component}:${pinInfo}`;
                designatorUidMap[designator] = node.uid;
                return designator;
            } else {
                const designator = node.name || node.uid;
                designatorUidMap[designator] = node.uid;
                return designator;
            }
        });
        const designatorsString = designators.join(", ");
        return {designators, designatorsString, designatorUidMap};
    }

    private getAnomalyUid(anomaly: {code: string; data: Record<string, unknown>}): ReviewCheck["uid"] {
        const hash = AliffHelpers.hash([anomaly.code, anomaly.data]);
        return `rch_${anomaly.code}_${hash}`;
    }
}
